import { useCssVars as _useCssVars, unref as _unref, defineComponent as _defineComponent } from 'vue'
import {
  inject,
  PropType,
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
} from "vue";
import { useLineStore } from "@/stores/lineStore";
import { useBookStore } from "@/stores/bookStore";
import { IBookInfo } from "@/domain/entities/book";
import { IBusStopData } from "@/domain/entities/busStop";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import {
  BoardCheckCode,
  BookIntervalCode,
  BookStatusName,
  KCPResult,
  PGCode,
  PROVIDE_KEY_REMOVE_LOADING_IMAGE,
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_LOADING_IMAGE,
  PROVIDE_KEY_SHOW_TOAST_MESSAGE,
  RemoveLoadingImage,
  ShowErrorModal,
  ShowLoadingImage,
  ShowToastMessage,
} from "@/types";
import NavigationBar from "@/components/shared/NavigationBar.vue";
import BookHistoryDetailInfo from "@/components/book/BookHistoryDetailInfo.vue";
import FooterButton from "@/components/shared/button/FooterButton.vue";
import MapOfBusStop from "@/views/map/MapOfBusStop.vue";
import QRCode from "@/components/shared/QRCode.vue";
import Modal from "@/components/shared/modal/base/Modal.vue";
import CancelKcp from "@/components/book/CancelKcp.vue";
import MultipleBookDateSeats from "@/components/book/MultipleBookDateSeats.vue";
import SingleBookDateSeats from "@/components/book/SingleBookDateSeats.vue";
import BookHistoryTotalAmount from "@/components/book/BookHistoryTotalAmount.vue";
import { useServiceStore } from "@/stores/serviceStore";
import ServiceEvaluationQuestions from "@/components/mypage/ServiceEvaluationQuestions.vue";
import { useSurveyStore } from "@/stores/surveyStore";
import { ISurvey } from "@/domain/entities/survey";
import { useUserStore } from "@/stores/userStore";
import ScrollTopControl from "@/components/shared/ScrollTopControl.vue";
import PageModal from "@/components/shared/modal/base/PageModal.vue";


const __default__ = {
  name: "BookHistoryDetail",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  bookInfo: {
    type: Object as PropType<IBookInfo>,
    required: true,
  },
  turnId: {
    type: String,
    required: true,
  },
  oneTimeFare: {
    type: Number,
    required: true,
  },
},
  emits: ["onClickClose", "onCompleteBookCancel"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

_useCssVars(_ctx => ({
  "0c8d6f70-footerHeight": (footerHeight.value)
}))

dayjs.locale("ko");
dayjs.extend(timezone);
dayjs.tz.setDefault("Asia/Seoul");

const props = __props;

const emit = __emit;

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;
const showToastMessage = inject(
  PROVIDE_KEY_SHOW_TOAST_MESSAGE
) as ShowToastMessage;
const showLoadingImage = inject(
  PROVIDE_KEY_SHOW_LOADING_IMAGE
) as ShowLoadingImage;
const removeLoadingImage = inject(
  PROVIDE_KEY_REMOVE_LOADING_IMAGE
) as RemoveLoadingImage;

const lineStore = useLineStore();
const bookStore = useBookStore();
const surveyStore = useSurveyStore();
const userStore = useUserStore();
const serviceStore = useServiceStore();
const serviceInfo = computed(() => serviceStore.serviceInfo);

const contentArea = ref<HTMLElement>();

const footerHeight = computed(() =>
  props.bookInfo.bookStatus === BookStatusName.BOOK || !!lineSurvey.value
    ? "70px"
    : "0px"
);
const shouldShowBookCancelModal = ref(false);
const showBookCancelModal = () => {
  shouldShowBookCancelModal.value = true;
};
const shouldShowUserRemainBookCount = computed(
  () => !!serviceInfo.value?.shouldShowRemainBookCount
);

const selectedBusStop = ref<IBusStopData | undefined>(undefined);
const shouldShowMap = ref(false);
const toggleShowMap = (show: boolean) => {
  shouldShowMap.value = show;
};

const onClickBusStopLocation = (busStopId: string) => {
  selectedBusStop.value = lineStore.getBusStopById(busStopId);
  toggleShowMap(true);
};

/** 예약 취소 처리 */
const cancelBookInProgress = ref(false);
const requestCancelBook = async () => {
  shouldShowBookCancelModal.value = false;
  cancelBookInProgress.value = true;
  showLoadingImage();
  const pgCode = props.bookInfo.pgCode;
  if (pgCode === PGCode.KCP) {
    cancelKcp();
  } else if (pgCode === PGCode.KakaoPay) {
    cancelKakaoPay();
  } else if (!pgCode) {
    cancelFreeBook();
  }
};

// 월예약 취소 모달
const isMonthlyBook =
  props.bookInfo.bookIntervalCode === BookIntervalCode.MONTH;
const isValidRefund = typeof props.bookInfo.cancelableAmount === "number";

const monthlyBookRefund = computed(() => {
  const cancelableAmount = props.bookInfo.cancelableAmount;
  if (typeof cancelableAmount !== "number") {
    return;
  }
  return cancelableAmount.toLocaleString();
});

const getUserRemainBookCount = async () => {
  if (!shouldShowUserRemainBookCount.value) return;

  const result = await userStore.getRemainBookCount();

  if (!result.isSuccess) {
    const { errorMessage, resultCode, errorTime } = result;
    showErrorModal(
      "잔여예약 횟수 조회 오류",
      errorMessage,
      resultCode,
      errorTime
    );
  }
};

const onCompleteBookCancel = () => {
  getUserRemainBookCount();
  emit("onCompleteBookCancel");
};

const cancelKcpRef = ref<InstanceType<typeof CancelKcp>>();
const cancelKcp = async () => {
  cancelKcpRef.value?.cancelKCP();
};
const onBookCancelResult = (result: KCPResult) => {
  const { resultCode, resultMessage } = result;
  cancelBookInProgress.value = false;
  removeLoadingImage();
  if (result.resultCode === "0") {
    onCompleteBookCancel();
    return;
  }
  showErrorModal(
    "KCP 결제 취소 오류",
    resultMessage || "예약취소에 실패하였습니다.",
    Number(resultCode)
  );
};

const cancelKakaoPay = async () => {
  const cancelKakaoPayResult = await bookStore.cancelKakaoPay(
    props.bookInfo.id,
    props.bookInfo.bookIntervalCode === BookIntervalCode.DAY
      ? props.bookInfo.bookDetailId
      : undefined
  );
  cancelBookInProgress.value = false;
  removeLoadingImage();
  if (cancelKakaoPayResult?.isSuccess) {
    onCompleteBookCancel();
    return;
  }
  showErrorModal(
    "카카오페이 결제 취소 오류",
    cancelKakaoPayResult?.errorMessage || "예약취소에 실패하였습니다.",
    cancelKakaoPayResult?.resultCode,
    cancelKakaoPayResult?.errorTime
  );
};

const cancelFreeBook = async () => {
  const cancelFreeBookResult = await bookStore.requestCancelBook(
    props.bookInfo.id,
    props.bookInfo.bookDetailId
  );
  cancelBookInProgress.value = false;
  removeLoadingImage();
  if (cancelFreeBookResult?.isSuccess) {
    onCompleteBookCancel();
    return;
  }
  showErrorModal(
    "예약취소 오류",
    cancelFreeBookResult?.errorMessage || "예약취소에 실패하였습니다.",
    cancelFreeBookResult?.resultCode,
    cancelFreeBookResult?.errorTime
  );
};

// 서비스 평가 처리
const serviceEvaluationQuestions =
  ref<InstanceType<typeof ServiceEvaluationQuestions>>();

const isEvaluable = computed(() => !!serviceEvaluationQuestions.value?.isValid);

/** 서비스 평가 조회하기 */
const lineSurvey = ref<ISurvey>();
const getServiceEvaluation = async () => {
  const lineSurveyResult = await surveyStore.getServiceEvaluationContents();
  if (!lineSurveyResult.isSuccess) return;

  lineSurvey.value = lineSurveyResult.data;
};

/** 서비스 평가 응답하기  */
const responseServiceEvaluation = async () => {
  if (!serviceEvaluationQuestions.value || !lineSurvey.value) return;

  const userAnswers = new Map(
    [...serviceEvaluationQuestions.value.userAnswers.entries()].sort()
  );

  const sendSurveyResult = await surveyStore.sendSurveyResult(
    lineSurvey.value.id,
    userAnswers,
    props.bookInfo.turnId,
    props.bookInfo.allocUid
  );

  if (!sendSurveyResult.isSuccess) {
    showErrorModal(
      "서비스평가 제출 오류",
      sendSurveyResult.errorMessage,
      sendSurveyResult.resultCode,
      sendSurveyResult.errorTime
    );
    return;
  }

  showToastMessage("서비스 평가가 완료되었습니다.", 3000, 350);
  getServiceEvaluation();
};

onMounted(() => {
  if (props.bookInfo.bookStatus === BookStatusName.BOARD) {
    getServiceEvaluation();
  }
});

const __returned__ = { props, emit, showErrorModal, showToastMessage, showLoadingImage, removeLoadingImage, lineStore, bookStore, surveyStore, userStore, serviceStore, serviceInfo, contentArea, footerHeight, shouldShowBookCancelModal, showBookCancelModal, shouldShowUserRemainBookCount, selectedBusStop, shouldShowMap, toggleShowMap, onClickBusStopLocation, cancelBookInProgress, requestCancelBook, isMonthlyBook, isValidRefund, monthlyBookRefund, getUserRemainBookCount, onCompleteBookCancel, cancelKcpRef, cancelKcp, onBookCancelResult, cancelKakaoPay, cancelFreeBook, serviceEvaluationQuestions, isEvaluable, lineSurvey, getServiceEvaluation, responseServiceEvaluation, get BoardCheckCode() { return BoardCheckCode }, get BookIntervalCode() { return BookIntervalCode }, get BookStatusName() { return BookStatusName }, NavigationBar, BookHistoryDetailInfo, FooterButton, MapOfBusStop, QRCode, Modal, CancelKcp, MultipleBookDateSeats, SingleBookDateSeats, BookHistoryTotalAmount, ServiceEvaluationQuestions, ScrollTopControl, PageModal }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})